import { Component, EventEmitter, Input, OnDestroy, OnInit, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { Grant, PERMISSION, Sharing, User, UserPermission, UserWithType } from '@ddv/models';
import { Subscription } from 'rxjs';

import { Entitlement, USER_GROUP_TOOLTIP_MAP } from '../../models/entitlement.constants';
import { UserEntitlements } from '../../models/user-entitlements';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-user-select',
    templateUrl: './user-select.component.html',
    styleUrls: ['./user-select.component.scss'],
    standalone: false,
})
export class UserSelectComponent implements OnInit, OnChanges, OnDestroy {
    @Input() clientCode: string | undefined;
    @Input() sharing: Sharing = { toUsers: [], toTeams: [] };
    @Input() title?: string;
    @Input() featureType?: 'DASHBOARD' | 'WIDGET';
    @Input() permission?: string;
    @Input() entitlements: UserEntitlements | undefined;
    @Input() isInCreateViewMode = false;
    @Input() allowToggleUserList = true;

    @Output() sharingChange = new EventEmitter<Sharing>();

    private readonly userService = inject(UserService);
    private readonly currentStateService = inject(CurrentStateService);

    // should be protected but are used in tests - START
    selectedTab = '';
    searchParam = '';
    userList: User[] = [];
    isInViewMode = false;
    selectedUsers: UserPermission[] = [];
    showUserList = false;
    // should be protected but are used in tests - END

    protected readonly tooltipMessageMap = USER_GROUP_TOOLTIP_MAP;

    private hasExternalShareEntitlement = false;
    private readonly subscription = new Subscription();

    ngOnInit(): void {
        this.selectedTab = 'ALL';
        this.subscription.add(
            this.currentStateService.dashboardModeAndId$.subscribe({
                next: ({ mode }) => this.isInViewMode = mode === 'view',
            }),
        );

        this.selectedUsers = this.toPermission();
        this.setTooltipDescriptions();

        this.hasExternalShareEntitlement = !!this.entitlements?.hasPermission(Entitlement.EXTERNAL_SHARE);

        this.userService.fetchUserListForPermissions(this.featureType, this.clientCode)
            .subscribe({
                next: (data) => {
                    this.convertToUserList(data);
                    this.mergeSelectedUsers();
                },
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sharing) {
            this.selectedUsers = this.toPermission();
            this.setTooltipDescriptions();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    toggleUserList(): void {
        if (this.isSharingReadonly()) {
            return;
        }

        if (!this.showUserList) {
            this.updateUserList();
        }

        this.showUserList = !this.showUserList;
    }

    applySelectedUsers(event: MouseEvent): void {
        event.stopPropagation();
        this.showUserList = false;
        this.searchParam = '';
        this.updateSelectedUsers();
    }

    onUserSelect(username: string): void {
        let privilege: string | undefined;
        let displayPrivilege: string | undefined;
        const userSelected = this.userList.find((user) => user.name === username);

        if (!userSelected) {
            return;
        }

        if (userSelected?.selected) {
            privilege = this.permission ?? '';
            displayPrivilege = this.getDisplayPrivilege(this.permission);
        } else {
            const existingUser = this.selectedUsers.find((exUser) => exUser.name.toLowerCase() === userSelected.name.toLowerCase());
            if (existingUser && existingUser.privilege !== this.permission) {
                privilege = existingUser.privilege;
                displayPrivilege = this.getDisplayPrivilege(existingUser.privilege);
            }
        }
        userSelected.privilege = privilege;
        userSelected.displayPrivilege = displayPrivilege;
    }

    removeUserPermission(event: MouseEvent, index: number): void {
        event.stopPropagation();

        if (this.isSharingReadonly()) {
            return;
        }

        this.showUserList = false;
        const removedUser = this.selectedUsers.splice(index, 1)[0];
        const pos = this.userList.findIndex((user) => user.name === removedUser?.name);
        if (pos !== -1) { // user might be selected in the UI but still missing from the received user list
            this.userList[pos].selected = false;
        }
        this.sharing = this.fromPermission();
        this.sharingChange.emit(this.sharing);
    }

    private isSharingReadonly(): boolean {
        return (this.featureType === 'DASHBOARD' &&
            !this.isInCreateViewMode &&
            this.isInViewMode) ||
            !this.allowToggleUserList;
    }

    private convertToUserList(data: UserWithType[]): void {
        this.userList = data.reduce((users: User[], user) => {
            if (this.includeUserInList(user)) {
                users.push({
                    id: undefined,
                    name: user.name,
                    email: user.email,
                    sharedType: user.type,
                    privilege: undefined,
                    selected: false,
                });
            }

            return users;
        }, []);
    }

    private mergeSelectedUsers(): void {
        this.selectedUsers?.forEach((selectedUser) => {
            if (!this.userList.find((user) => user.name.toLowerCase() === selectedUser.name.toLowerCase())) {
                selectedUser.isMissing = true; // user is selected but missing in the received user list
            }
        });

        this.userList.forEach((user) => {
            const existingUser = this.selectedUsers.find((exUser) => exUser.name.toLowerCase() === user.name.toLowerCase());
            if (existingUser) {
                user.id = (existingUser.privilege === this.permission) ? existingUser.id : undefined;
                user.selected = existingUser.privilege === this.permission;
                user.privilege = existingUser.privilege;
                user.displayPrivilege = this.getDisplayPrivilege(existingUser.privilege);
            } else if (user.isMissing) {
                user.selected = true;
            }
        });
    }

    private updateUserList(): void {
        if (!this.userList.length && !this.selectedUsers.length) {
            return;
        }
        this.userList.forEach((user) => {
            user.privilege = '';
            user.selected = false;
        });

        this.selectedUsers.forEach((selectedUser) => {
            const user = this.userList.find((u) => u.name.toLowerCase() === selectedUser.name.toLowerCase());
            if (user) {
                user.selected = selectedUser.privilege === this.permission;
                user.privilege = selectedUser.privilege;
                user.displayPrivilege = this.getDisplayPrivilege(selectedUser.privilege);
            }
        });
    }

    private updateSelectedUsers(): void {
        const currentSelectedUsers = this.userList.reduce((selectedUsers: UserPermission[], user) => {
            if (user.selected) {
                selectedUsers.push({
                    id: user.id,
                    name: user.name,
                    privilege: user.privilege,
                    sharedType: user.sharedType,
                });
            }
            return selectedUsers;
        }, []);

        this.selectedUsers = this.selectedUsers.filter((selectedUser) => (selectedUser.privilege !== this.permission &&
            !currentSelectedUsers.find((currentUser) => currentUser.name.toLowerCase() === selectedUser.name.toLowerCase())) ||
            selectedUser.isMissing);
        this.selectedUsers = [...this.selectedUsers, ...currentSelectedUsers];
        this.sharing = this.fromPermission();
        this.sharingChange.emit(this.sharing);
    }

    private getDisplayPrivilege(privilege?: string): string {
        return privilege === PERMISSION.EDIT ? 'View & Edit' : 'View Only';
    }

    private includeUserInList(user: UserWithType): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return this.hasExternalShareEntitlement || (user.internal || (user.type === 'TEAM' && user.name !== 'Client Users'));
    }

    private toPermission(): UserPermission[] {
        const users: UserPermission[] = this.sharing.toUsers.map((user) => ({
            name: user.name,
            privilege: user.permission === 'write' ? 'EDIT' : 'VIEW',
            sharedType: 'USER',
        }));
        const teams: UserPermission[] = this.sharing.toTeams.map((team) => ({
            name: team.name,
            privilege: team.permission === 'write' ? 'EDIT' : 'VIEW',
            sharedType: 'TEAM',
        }));
        return [...users, ...teams];
    }

    private fromPermission(): Sharing {
        const toUsers: Grant[] = this.selectedUsers
            .filter((p) => p.sharedType === 'USER')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' }));
        const toTeams: Grant[] = this.selectedUsers
            .filter((p) => p.sharedType === 'TEAM')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' }));
        return { toUsers, toTeams };
    }

    private setTooltipDescriptions(): void {
        this.selectedUsers.forEach((u) => {
            const nameInUpperCase = u.name.toUpperCase();
            const tooltipMessage = this.tooltipMessageMap.get(nameInUpperCase);

            if (tooltipMessage) {
                u.tooltipDescription = { message: tooltipMessage };
            }
        });
    }
}
